import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Package, Search, RefreshCw, AlertTriangle, CheckCircle, Upload,
  Lock, History, ArrowDown, ArrowUp, Minus, Layers, Tag, Download,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function OpsInventory() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Navigation state
  const [mainTab, setMainTab] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [categoryBrands, setCategoryBrands] = useState([]);

  // History
  const [historySummary, setHistorySummary] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [itemsRes, catsRes, brandsRes] = await Promise.all([
        api.get("/ops-inventory/items"),
        api.get("/ops-inventory/categories"),
        api.get("/ops-inventory/brands"),
      ]);
      setItems(itemsRes.data.items || []);
      setCategories(catsRes.data.categories || []);
      setBrands(brandsRes.data.brands || []);
    } catch (e) {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryBrands = async (cat) => {
    try {
      const res = await api.get(`/ops-inventory/brands?category=${encodeURIComponent(cat)}`);
      setCategoryBrands(res.data.brands || []);
    } catch { setCategoryBrands([]); }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/ops-inventory/history/summary?days=7");
      setHistorySummary(res.data.summary || []);
    } catch { toast.error("Failed to load history"); }
    finally { setHistoryLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/ops-inventory/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSyncOblik = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/ops-inventory/sync-oblik");
      toast.success(res.data.message);
      await loadAll();
    } catch { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };

  const handleExport = () => {
    let url = `${BACKEND_URL}/api/ops-inventory/export`;
    const params = [];
    if (mainTab === "categories" && selectedCategory) params.push(`category=${encodeURIComponent(selectedCategory)}`);
    if ((mainTab === "categories" || mainTab === "brands") && selectedBrand && selectedBrand !== "all") params.push(`brand=${encodeURIComponent(selectedBrand)}`);
    if (params.length) url += `?${params.join("&")}`;
    window.open(url, "_blank");
  };

  // Filtering logic
  const getFilteredItems = useCallback(() => {
    let filtered = [...items];
    if (mainTab === "categories" && selectedCategory) {
      filtered = filtered.filter(i => i.category === selectedCategory);
      if (selectedBrand && selectedBrand !== "all") {
        filtered = filtered.filter(i => i.brand === selectedBrand);
      }
    } else if (mainTab === "brands" && selectedBrand) {
      filtered = filtered.filter(i => i.brand === selectedBrand);
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter(i =>
        (i.product_name || "").toLowerCase().includes(s) ||
        (i.brand || "").toLowerCase().includes(s) ||
        (i.category || "").toLowerCase().includes(s)
      );
    }
    return filtered;
  }, [items, mainTab, selectedCategory, selectedBrand, searchTerm]);

  const filteredItems = getFilteredItems();

  const getStats = (list) => ({
    total: list.length,
    totalUnits: list.reduce((s, i) => s + (i.quantity || 0), 0),
    lowStock: list.filter(i => i.quantity > 0 && i.quantity <= 5).length,
    outOfStock: list.filter(i => i.quantity === 0).length,
  });

  const stats = getStats(filteredItems);

  const getStockBadge = (qty) => {
    if (qty === 0) return <Badge className="bg-red-100 text-red-700 text-xs">Out</Badge>;
    if (qty <= 5) return <Badge className="bg-amber-100 text-amber-700 text-xs">Low</Badge>;
    return <Badge className="bg-emerald-100 text-emerald-700 text-xs">OK</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ops-inventory-page">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8 text-blue-500" />
            <div><p className="text-2xl font-bold font-mono">{stats.total}</p><p className="text-xs text-slate-500">Total SKUs</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
            <div><p className="text-2xl font-bold font-mono">{stats.totalUnits}</p><p className="text-xs text-slate-500">Total Units</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
            <div><p className="text-2xl font-bold font-mono">{stats.lowStock}</p><p className="text-xs text-slate-500">Low Stock</p></div>
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-500" />
            <div><p className="text-2xl font-bold font-mono">{stats.outOfStock}</p><p className="text-xs text-slate-500">Out of Stock</p></div>
          </div>
        </CardContent></Card>
      </div>

      {/* Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2 items-center justify-between">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search products..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="ops-inv-search" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={handleSyncOblik} disabled={syncing} data-testid="sync-oblik-btn">
                {syncing ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Sync OBLIK
              </Button>
              <Button variant="outline" size="icon" onClick={loadAll} title="Refresh" data-testid="ops-inv-refresh">
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport} data-testid="ops-inv-export-btn">
                <Download className="w-4 h-4 mr-1" /> Export
              </Button>
              <div className="relative">
                <Input type="file" accept=".csv,.xlsx,.xls" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleUpload} disabled={uploading} data-testid="ops-inv-upload-input" />
                <Button disabled={uploading} size="sm" data-testid="ops-inv-upload-btn">
                  {uploading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  Import
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={mainTab} onValueChange={(v) => { setMainTab(v); setSelectedCategory(null); setSelectedBrand(null); if (v === "history") loadHistory(); }}>
        <TabsList data-testid="ops-inv-main-tabs">
          <TabsTrigger value="all" data-testid="ops-tab-all">All</TabsTrigger>
          <TabsTrigger value="categories" data-testid="ops-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="brands" data-testid="ops-tab-brands">Brands</TabsTrigger>
          <TabsTrigger value="history" data-testid="ops-tab-history">
            <History className="w-4 h-4 mr-1" /> History
          </TabsTrigger>
        </TabsList>

        {/* ALL tab */}
        <TabsContent value="all">
          <InventoryTable items={filteredItems} getStockBadge={getStockBadge} />
        </TabsContent>

        {/* CATEGORIES tab */}
        <TabsContent value="categories">
          {!selectedCategory ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="category-grid">
              {categories.length === 0 ? (
                <p className="text-slate-500 col-span-full text-center py-8">No categories found. Upload inventory to get started.</p>
              ) : categories.map(cat => {
                const catItems = items.filter(i => i.category === cat);
                return (
                  <Card key={cat} className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-blue-300"
                    onClick={() => { setSelectedCategory(cat); loadCategoryBrands(cat); setSelectedBrand(null); }}
                    data-testid={`category-card-${cat}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Layers className="w-5 h-5 text-blue-500" />
                        <h3 className="font-semibold text-sm">{cat}</h3>
                      </div>
                      <p className="text-2xl font-bold font-mono">{catItems.length}</p>
                      <p className="text-xs text-slate-500">{catItems.reduce((s, i) => s + (i.quantity || 0), 0)} units</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => { setSelectedCategory(null); setSelectedBrand(null); }} data-testid="back-to-categories">
                  Back
                </Button>
                <Badge variant="secondary" className="text-sm">{selectedCategory}</Badge>
                <div className="flex gap-1 flex-wrap">
                  <Button size="sm" variant={!selectedBrand || selectedBrand === "all" ? "default" : "outline"}
                    onClick={() => setSelectedBrand(null)} data-testid="cat-brand-all">All</Button>
                  {categoryBrands.map(b => (
                    <Button key={b} size="sm" variant={selectedBrand === b ? "default" : "outline"}
                      onClick={() => setSelectedBrand(b)} data-testid={`cat-brand-${b}`}>
                      {b}
                    </Button>
                  ))}
                </div>
              </div>
              <InventoryTable items={filteredItems} getStockBadge={getStockBadge} />
            </div>
          )}
        </TabsContent>

        {/* BRANDS tab */}
        <TabsContent value="brands">
          {!selectedBrand ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="brand-grid">
              {brands.length === 0 ? (
                <p className="text-slate-500 col-span-full text-center py-8">No brands found. Upload inventory to get started.</p>
              ) : brands.map(brand => {
                const bItems = items.filter(i => i.brand === brand);
                return (
                  <Card key={brand} className="cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-indigo-300"
                    onClick={() => setSelectedBrand(brand)}
                    data-testid={`brand-card-${brand}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className="w-5 h-5 text-indigo-500" />
                        <h3 className="font-semibold text-sm">{brand}</h3>
                      </div>
                      <p className="text-2xl font-bold font-mono">{bItems.length}</p>
                      <p className="text-xs text-slate-500">{bItems.reduce((s, i) => s + (i.quantity || 0), 0)} units</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedBrand(null)} data-testid="back-to-brands">
                  Back
                </Button>
                <Badge variant="secondary" className="text-sm">{selectedBrand}</Badge>
              </div>
              <InventoryTable items={filteredItems} getStockBadge={getStockBadge} />
            </div>
          )}
        </TabsContent>

        {/* HISTORY tab */}
        <TabsContent value="history">
          <HistoryPanel summary={historySummary} loading={historyLoading} onRefresh={loadHistory} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


function InventoryTable({ items, getStockBadge }) {
  if (items.length === 0) {
    return (
      <Card>
        <div className="p-12 text-center text-slate-500">
          <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-lg">No inventory items found</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="py-3 px-4 border-b">
        <CardTitle className="text-sm font-medium">
          Items ({items.length})
          <span className="text-slate-400 font-normal ml-2">
            {items.reduce((s, i) => s + (i.quantity || 0), 0)} total units
          </span>
        </CardTitle>
      </CardHeader>
      <ScrollArea className="h-[450px]">
        <Table className="data-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase w-12">#</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase">Product</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase w-24">Brand</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase w-24">Category</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-20">Qty</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right w-24">MRP</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-16">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, idx) => (
              <TableRow key={item.product_name + idx}
                className={`${item.quantity === 0 ? 'bg-red-50' : ''} ${item.quantity > 0 && item.quantity <= 5 ? 'bg-amber-50' : ''}`}
              >
                <TableCell className="font-mono text-sm">{item.sr_no || idx + 1}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{item.product_name}</p>
                    {item.locked && <Lock className="w-3 h-3 text-slate-400" title="Synced from Fulfillment (locked)" />}
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{item.brand}</Badge></TableCell>
                <TableCell className="text-xs text-slate-600">{item.category}</TableCell>
                <TableCell className="text-center font-mono text-sm font-semibold">{item.quantity}</TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {item.mrp ? `Rs. ${item.mrp.toLocaleString()}` : "-"}
                </TableCell>
                <TableCell className="text-center">{getStockBadge(item.quantity)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>
    </Card>
  );
}


function HistoryPanel({ summary, loading, onRefresh }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const eventLabel = (type) => {
    if (type === "shipping_deduction") return { text: "Shipping Deduction", icon: <ArrowDown className="w-4 h-4 text-red-500" />, color: "text-red-600" };
    if (type === "rto_addition") return { text: "RTO Return", icon: <ArrowUp className="w-4 h-4 text-green-500" />, color: "text-green-600" };
    return { text: type, icon: <Minus className="w-4 h-4" />, color: "text-slate-600" };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Last 7 Days Activity</h3>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-history">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {summary.length === 0 ? (
        <Card>
          <div className="p-12 text-center text-slate-500">
            <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No inventory changes recorded in the last 7 days</p>
          </div>
        </Card>
      ) : summary.map(day => (
        <Card key={day.date} data-testid={`history-day-${day.date}`}>
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-sm font-medium">{day.date}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {day.events.map((evt, eIdx) => {
              const evtInfo = eventLabel(evt.event_type);
              return (
                <div key={eIdx} className="border-b last:border-b-0">
                  <div className="flex items-center justify-between px-4 py-2 bg-slate-50">
                    <div className="flex items-center gap-2">
                      {evtInfo.icon}
                      <span className={`text-sm font-medium ${evtInfo.color}`}>{evtInfo.text}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span>{evt.items_affected} items</span>
                      <span className={`font-mono font-semibold ${evt.total_change < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {evt.total_change > 0 ? '+' : ''}{evt.total_change}
                      </span>
                    </div>
                  </div>
                  <div className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {evt.details.slice(0, 10).map((d, dIdx) => (
                        <Badge key={dIdx} variant="outline" className="text-xs">
                          {d.product_name} ({d.quantity_change > 0 ? '+' : ''}{d.quantity_change}) → {d.new_quantity}
                        </Badge>
                      ))}
                      {evt.details.length > 10 && (
                        <Badge variant="outline" className="text-xs text-slate-400">+{evt.details.length - 10} more</Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
